package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * A workspace as the person signed in sees it.
 *
 * This replaces the bare `OrgDto` in `MeResponse` because every role-gated surface needs to know
 * three things before it can draw anything: which workspace, what the viewer is inside it, and what
 * the workspace is paying for. Splitting those across a second `GET /me/roles` call would open a
 * window — however short — in which the navigation is drawn for the wrong role, and a translator
 * briefly seeing an owner's menu is exactly the failure this type exists to prevent.
 */
@Serializable
data class WorkspaceDto(
    val id: String,
    val slug: String,
    val name: String,
    /** What the person asking holds here. */
    val role: Role,
    val plan: Plan,
    val planStatus: PlanStatus,
    val memberCount: Int,
    val projectCount: Int,
    /**
     * Kept from the `OrgDto` this type replaced.
     *
     * A browser with the app already open holds the previous build until it reloads, and that build
     * deserializes this list as `OrgDto`, where `createdAt` has no default. Dropping the field would
     * turn every open tab's next `/auth/me` into a session error for the length of a deploy — a real
     * failure, for a field that costs one column.
     */
    val createdAt: String = "",
)

/**
 * Whether this role occupies a paid seat.
 *
 * The single definition of "billable", used by the seat query, the price preview and the members
 * page. It mentions role and nothing else — in particular it does not mention the reviewer flag,
 * which is what keeps [MemberCapabilities.canToggleApprove] from ever costing money.
 */
val Role.isBillable: Boolean
    get() = this == Role.OWNER || this == Role.ADMIN

/**
 * Whether this role carries at least the authority of [required].
 *
 * Leans on the declaration order of [Role] — OWNER, ADMIN, TRANSLATOR, VIEWER — which is descending
 * authority, so a lower ordinal is a stronger role. Stated here once so no call site has to
 * rediscover that, and so reordering the enum breaks in one place rather than silently granting a
 * viewer an owner's menu.
 */
fun Role.atLeast(required: Role): Boolean = ordinal <= required.ordinal

/**
 * Whether this member may move a translation to APPROVED.
 *
 * [override] is `memberships.can_approve`, which is nullable on purpose: null means "follow the
 * role", so a workspace that has never thought about reviewers behaves exactly as it did before the
 * column existed. Set explicitly, it makes a trusted translator a **Reviewer** without promoting
 * them to admin — approval rights should not cost $19 a month, or teams will skip review to save
 * the seat.
 */
fun canApprove(role: Role, override: Boolean?): Boolean = override ?: role.isBillable

/**
 * What the caller may do to one member, decided once and used everywhere.
 *
 * A single `canManage` boolean is precisely how an interface ends up offering OWNER to an admin: the
 * flag says "you manage members", the select renders every role, and the server refuses the one the
 * customer picked. Anything the API would refuse must be absent here, with [blockedReason] carrying
 * the explanation when a control is visible but unusable.
 */
@Serializable
data class MemberCapabilities(
    /** True when [assignableRoles] holds anything other than the role already held. */
    val canChangeRole: Boolean = false,
    /**
     * Every role this caller may move this member to, including the one they already hold so a
     * select can show its current value. Never contains a role the server would refuse.
     */
    val assignableRoles: List<Role> = emptyList(),
    val canRemove: Boolean = false,
    val canTransferOwnership: Boolean = false,
    /** Only ever true for a TRANSLATOR: the reviewer flag is meaningless on the other roles. */
    val canToggleApprove: Boolean = false,
    /**
     * Why the choices above are narrower than they look, in words a customer can act on. Null when
     * nothing is being withheld.
     */
    val blockedReason: String? = null,
)

/**
 * Whether a role change adds a billable seat (+1), removes one (-1), or costs nothing (0).
 *
 * Not a permission question — [capabilitiesFor] answers those — but the one the confirmation modal
 * needs, because the money has to be stated before the click. A single stray click on a select used
 * to add a $19/month seat with no confirmation and no price, discovered on an invoice.
 */
fun seatEffect(from: Role, to: Role): Int = when {
    !from.isBillable && to.isBillable -> 1
    from.isBillable && !to.isBillable -> -1
    else -> 0
}

/** Shown when an admin wonders why OWNER is not in the list. */
const val OWNER_APPOINTMENT_REASON = "Only an owner can appoint an owner."

/** Shown on the last owner's row, where every role change and removal is refused. */
const val LAST_OWNER_REASON =
    "Make someone else an owner first — a workspace cannot be left with nobody able to manage it."

/** Why a promotion into a paid seat is unavailable, quoting what the plan actually includes. */
fun seatCapReason(plan: Plan): String {
    val limit = plan.maxBillableSeats ?: return ""
    val seats = if (limit == 1) "seat" else "seats"
    return "The ${plan.displayName} plan includes $limit owner or admin $seats. " +
        "Translator and viewer seats are unlimited on every plan."
}

/**
 * The one function that decides who may do what to whom.
 *
 * The server calls it to build the members response and the web app reads the result — deliberately
 * the same code, because two implementations of "may an admin appoint an owner?" is one
 * implementation and one bug. The parameters are the whole of the context the answer depends on:
 *
 * @param callerRole what the person asking holds, or null if they are not a member at all.
 * @param targetRole what the member being described holds.
 * @param isSelf whether those are the same person.
 * @param ownerCount how many owners the workspace has, which decides whether this one is load-bearing.
 * @param billableSeats owner and admin seats currently occupied, the reviewer flag playing no part.
 * @param plan what the workspace is paying for, and therefore how many seats it may fill.
 */
fun capabilitiesFor(
    callerRole: Role?,
    targetRole: Role,
    isSelf: Boolean,
    ownerCount: Int,
    billableSeats: Int,
    plan: Plan,
): MemberCapabilities {
    // Translators and viewers are offered no controls at all — not disabled ones. A row of greyed
    // buttons invites someone to keep clicking; an absent control is understood immediately.
    if (callerRole == null || !callerRole.isBillable) return MemberCapabilities()

    // The last owner is frozen: not demotable, not removable, by anyone including themselves.
    if (targetRole == Role.OWNER && ownerCount <= 1) {
        return MemberCapabilities(
            assignableRoles = listOf(Role.OWNER),
            blockedReason = LAST_OWNER_REASON,
        )
    }

    // An owner's row is an owner's business. An admin cannot appoint one, so an admin must not be
    // able to demote or delete one either — otherwise the role they cannot grant is the role they
    // can strip, and two admins can quietly reduce a workspace to its last owner.
    if (targetRole == Role.OWNER && callerRole != Role.OWNER) {
        return MemberCapabilities(
            assignableRoles = listOf(Role.OWNER),
            canToggleApprove = false,
            blockedReason = OWNER_APPOINTMENT_REASON,
        )
    }

    // Someone who already holds a paid seat keeps it when they move between OWNER and ADMIN, so the
    // cap only bites when a free role is being promoted into a paid one.
    val limit = plan.maxBillableSeats
    val seatAvailable = limit == null || targetRole.isBillable || billableSeats < limit

    val assignableRoles = Role.entries.filter { role ->
        when {
            // Always offered: it is what they already hold, so a select can render it.
            role == targetRole -> true
            role == Role.OWNER && callerRole != Role.OWNER -> false
            role.isBillable && !seatAvailable -> false
            else -> true
        }
    }

    return MemberCapabilities(
        canChangeRole = assignableRoles.any { it != targetRole },
        assignableRoles = assignableRoles,
        // An admin may not remove an owner. Without this, the role they cannot grant is one they
        // can delete, which is the same privilege problem viewed from the other end.
        canRemove = !isSelf && (callerRole == Role.OWNER || targetRole != Role.OWNER),
        canTransferOwnership = callerRole == Role.OWNER && !isSelf && targetRole != Role.OWNER,
        canToggleApprove = targetRole == Role.TRANSLATOR,
        blockedReason = when {
            !seatAvailable -> seatCapReason(plan)
            callerRole != Role.OWNER -> OWNER_APPOINTMENT_REASON
            else -> null
        },
    )
}

/** Renaming a workspace. */
@Serializable
data class UpdateWorkspaceRequest(
    val name: String,
    /**
     * A new slug, when the customer wants one.
     *
     * Safe to change: a workspace slug appears only in settings URLs. Project links and
     * `lingvo.yaml` reference the globally-unique *project* slug, so a rename breaks nothing anyone
     * has written down — worth saying on the form, because people reasonably assume otherwise and
     * therefore never rename.
     */
    val slug: String? = null,
)

/** Creating a workspace on purpose, as opposed to the personal one everyone gets on signup. */
@Serializable
data class CreateWorkspaceRequest(val name: String)

/**
 * What a workspace has been doing, and where it is heading.
 *
 * **Every field is computed from what the product already recorded** — a history row per save since
 * the first release, a credit event per reservation since V24. The data to answer "how fast are we
 * going, what is it costing, and when will German be done" existed the whole time; nothing asked.
 */
@Serializable
data class InsightsDto(
    val days: Int,
    /** Saves per day, split by where the text came from: HUMAN, MACHINE, MEMORY, IMPORT, AGENT. */
    val throughput: List<InsightsSeriesDto> = emptyList(),
    /** Credits per day by provider, net of refunds. */
    val spend: List<InsightsSeriesDto> = emptyList(),
    val locales: List<LocaleInsightDto> = emptyList(),
    val stringsTranslated: Int = 0,
    val creditsSpent: Int = 0,
)

@Serializable
data class InsightsSeriesDto(val name: String, val points: List<InsightsPointDto> = emptyList())

@Serializable
data class InsightsPointDto(val label: String, val value: Double)

@Serializable
data class LocaleInsightDto(
    val locale: String,
    val total: Int,
    val translated: Int,
    val approved: Int,
    /** Distinct strings finished in the window — the rate a forecast divides by. */
    val recent: Int,
    /**
     * Days until this language is finished at the observed rate, or null when it is done or
     * standing still.
     *
     * **This is the number a release manager actually needs.** A percentage says they are at 62%
     * and nothing about whether that is three days or three months away.
     */
    val daysToFinish: Int? = null,
)
